import { FC, useCallback, useMemo, useState } from "react";
import { FixedSizeList as List } from "react-window";

import { TriangleDownIcon } from "@chakra-ui/icons";
import {
  Button,
  chakra,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  useMultiStyleConfig,
  Box,
} from "@chakra-ui/react";

import FlagIcon from "./FlagIcon";
import PhoneCodeOption from "./PhoneCodeOption";
import type { TPhoneCodeOption, TPhoneCodeSelectProps } from "./types";
import { usePhoneCodeListSearch } from "./utils/usePhoneCodeListSearch";

const popoverCssSelector = "& .chakra-popover__popper";

const PhoneCodeSelect: FC<TPhoneCodeSelectProps> = ({ id, options, country, isDisabled, onChange }) => {
  const styles = useMultiStyleConfig("PhoneInput", {});

  const [isOpen, setIsOpen] = useState(false);

  // If no value, select first option
  const selectedOption = useMemo(() => {
    const option = options.find(({ shortName }) => shortName === country);
    return option || options[0];
  }, [options, country]);

  const handleSelect = useCallback(
    ({ shortName }: TPhoneCodeOption) => {
      onChange(shortName);
      setIsOpen(false);
    },
    [onChange]
  );
  const title = [selectedOption?.label, selectedOption?.code].filter(Boolean).join(" ");

  const { searchOptions } = usePhoneCodeListSearch(options, isOpen);

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <Popover
      id={id}
      isOpen={isOpen}
      onClose={handleClose}
      closeOnBlur
      isLazy
      lazyBehavior="keepMounted"
      placement="bottom-start"
      preventOverflow={false}
      flip
    >
      <PopoverTrigger>
        <Button
          leftIcon={<FlagIcon option={selectedOption} />}
          iconSpacing="0.2rem"
          rightIcon={<TriangleDownIcon boxSize="0.5rem" mr="0.25rem" />}
          sx={styles.codeSelectButton}
          title={title}
          isDisabled={isDisabled}
          fontSize={selectedOption?.code.length > 4 ? "0.75rem" : undefined}
          letterSpacing={selectedOption?.code.length > 4 ? "-1px" : undefined}
          onClick={() => setIsOpen(!isOpen)}
        >
          <chakra.span sx={styles.codeSelectButtonLabel}>{selectedOption?.code}</chakra.span>
        </Button>
      </PopoverTrigger>
      <Portal>
        <Box sx={{ [popoverCssSelector]: { zIndex: 3000 } }}>
          <PopoverContent sx={styles.codeSelectList} tabIndex={0}>
            <List
              width="auto"
              height={16 * 20.5}
              style={{ maxHeight: `${16 * 20.5}px`, height: "auto" }}
              itemCount={searchOptions.length}
              itemSize={48}
              itemData={{ options: searchOptions, onSelect: handleSelect }}
            >
              {(props) => <PhoneCodeOption {...props} />}
            </List>
          </PopoverContent>
        </Box>
      </Portal>
    </Popover>
  );
};
export default PhoneCodeSelect;
